import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ApiConnector from "../../connector/ApiConnector";
import ConfigService from "../../service/ConfigService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";

const TOP_N = 10;
const GREEN_DISC_THRESHOLD = 1.2;
const POLL_INTERVAL_MS = 5000;

const discriminationColor = (ratio) => {
  if (ratio == null) return "#888";
  if (ratio > GREEN_DISC_THRESHOLD) return "#2e7d32";
  if (ratio >= 1.0) return "#f57c00";
  return "#c62828";
};

const formatNumber = (value, fractionDigits = 4) =>
  value == null ? "—" : Number(value).toFixed(fractionDigits);

const shortRuleName = (ruleName) =>
  ruleName ? ruleName.replace(/_rule_score$/, "") : ruleName;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const Component = () => {
  const [analysis, setAnalysis] = useState(null); // full RuleImpactAnalysisHttpResponse
  const [expanded, setExpanded] = useState(false);
  const [modalState, setModalState] = useState(null);
  const [selectedRules, setSelectedRules] = useState({});
  const [applying, setApplying] = useState(false);

  // Track the last seen `created` timestamp to detect cache updates
  const lastCreatedRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const poll = () => {
      ApiConnector.getRuleImpact()
        .then((response) => {
          if (!mounted) return;
          // Only update state when the cache has changed (new `created` timestamp)
          // or on first load. This avoids unnecessary re-renders every 5 seconds.
          const newCreated = response?.created ?? null;
          if (newCreated !== lastCreatedRef.current) {
            lastCreatedRef.current = newCreated;
            setAnalysis(response);
            setExpanded(false);
          }
        })
        .catch(() => {
          // Keep showing stale data on transient errors
        });
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const rules = analysis?.rules ?? [];
  const visibleRows = expanded ? rules : rules.slice(0, TOP_N);
  const hasMore = rules.length > TOP_N;

  const handleApplyClick = () => {
    setSelectedRules({});
    setModalState({ loading: true, data: null, error: null });

    ApiConnector.getBestConfigWeights()
      .then((response) => {
        if (
          !response ||
          !response.ruleWeights ||
          response.ruleWeights.length === 0
        ) {
          setModalState({
            loading: false,
            data: null,
            error: "No best-performing config found for the configured date range.",
          });
          return;
        }
        const preselected = {};
        response.ruleWeights.forEach((rw) => {
          if (
            rw.weightFieldName &&
            rw.weight != null &&
            rw.discriminationRatio != null &&
            rw.discriminationRatio > GREEN_DISC_THRESHOLD
          ) {
            preselected[rw.weightFieldName] = true;
          }
        });
        setSelectedRules(preselected);
        setModalState({ loading: false, data: response, error: null });
      })
      .catch(() => {
        setModalState({
          loading: false,
          data: null,
          error: "Failed to load best config weights.",
        });
      });
  };

  const handleToggleRule = (weightFieldName) => {
    setSelectedRules((prev) => ({
      ...prev,
      [weightFieldName]: !prev[weightFieldName],
    }));
  };

  const handleCancel = () => {
    setModalState(null);
    setSelectedRules({});
  };

  const handleConfirm = async () => {
    if (!modalState || !modalState.data) return;
    setApplying(true);
    try {
      const latest = await ApiConnector.getLatestConfig();
      if (!latest) return;
      if (!latest.riskEvaluation) latest.riskEvaluation = {};
      const newManual = {};
      modalState.data.ruleWeights.forEach((rw) => {
        if (
          rw.weightFieldName &&
          rw.weight != null &&
          selectedRules[rw.weightFieldName]
        ) {
          newManual[rw.weightFieldName] = rw.weight;
        }
      });
      latest.riskEvaluation.weightManualConfig = newManual;
      const result = await ConfigService.createConfig(latest, latest.id);
      if (result) {
        setModalState(null);
        setSelectedRules({});
      }
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = useMemo(
    () => Object.values(selectedRules).filter(Boolean).length,
    [selectedRules],
  );

  return (
    <div style={{ fontSize: "0.85em" }}>
      <div
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          marginBottom: "8px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", color: "#888" }}>
          {analysis?.trnDateMin && analysis?.trnDateMax && (
            <span>
              Range: {formatDate(analysis.trnDateMin)} – {formatDate(analysis.trnDateMax)}
            </span>
          )}
          <span>
            {analysis?.created
              ? `Last updated: ${formatDateTime(analysis.created)}`
              : "Not yet computed"}
          </span>
        </div>
        <button
          onClick={handleApplyClick}
          disabled={
            !rules.length || (modalState && modalState.loading)
          }
          title="Apply weights from the best config in the configured date range"
        >
          Apply rules
        </button>
      </div>

      {analysis === null ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#555" }}>
          <FontAwesomeIcon icon={faCircleNotch} spin={true} style={{ color: "#1565c0" }} />
          Loading…
        </div>
      ) : rules.length === 0 ? (
        <p style={{ color: "#888" }}>
          No data available. Configure the date range in the admin section and click &ldquo;Analyze now&rdquo; or wait until the analyze is complete.
        </p>
      ) : (
        <table
          className="config-table"
          style={{ width: "100%", tableLayout: "fixed" }}
        >
          <colgroup>
            <col style={{ width: "28px" }} />
            <col />
            <col style={{ width: "55px" }} />
            <col style={{ width: "55px" }} />
            <col style={{ width: "65px" }} />
            <col style={{ width: "70px" }} />
            <col style={{ width: "55px" }} />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Rule</th>
              <th title="Number of fraud transactions where this rule scored > 0">
                Hits
              </th>
              <th title="Fraud hits as a percentage of total fraud count">
                Hit %
              </th>
              <th title="Average score on fraud transactions (score > 0)">
                Avg F
              </th>
              <th title="Average score on non-fraud transactions (score > 0)">
                Avg NF
              </th>
              <th title="Discrimination ratio = Avg F / Avg NF (> 1 means good discriminator)">
                Disc.
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, idx) => {
              const isTop = idx < TOP_N;
              return (
                <tr
                  key={row.ruleName}
                  style={isTop ? { fontWeight: "bold" } : undefined}
                >
                  <td>{idx + 1}</td>
                  <td
                    title={row.ruleName}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {shortRuleName(row.ruleName)}
                  </td>
                  <td>{row.fraudHitCount}</td>
                  <td>{formatNumber(row.fraudHitPct, 1)}</td>
                  <td>{formatNumber(row.avgScoreFraud, 2)}</td>
                  <td>{formatNumber(row.avgScoreNonFraud, 2)}</td>
                  <td
                    style={{
                      color: discriminationColor(row.discriminationRatio),
                    }}
                  >
                    {formatNumber(row.discriminationRatio, 2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: "-5px" }}>
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="info-icon"
            onClick={() => setExpanded((v) => !v)}
            title={
              expanded
                ? "Show top 10 rules only"
                : `Show all ${rules.length} rules`
            }
            style={{ cursor: "pointer" }}
          />
        </div>
      )}

      {modalState &&
        ReactDOM.createPortal(
          <ApplyWeightsModal
            state={modalState}
            selectedRules={selectedRules}
            onToggleRule={handleToggleRule}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            applying={applying}
            selectedCount={selectedCount}
          />,
          document.body,
        )}
    </div>
  );
};

const ApplyWeightsModal = ({
  state,
  selectedRules,
  onToggleRule,
  onCancel,
  onConfirm,
  applying,
  selectedCount,
}) => {
  const { loading, data, error } = state;
  const score = data?.score || {};
  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !applying) onCancel();
      }}
    >
      <div className="modal-box" style={{ fontSize: "0.85em" }}>
        <h3>Apply weights from best config</h3>

        {loading && (
          <div
            style={{
              padding: "2.5rem 1rem",
              textAlign: "center",
              color: "#555",
            }}
          >
            <FontAwesomeIcon
              icon={faCircleNotch}
              spin={true}
              style={{
                color: "#1565c0",
                fontSize: "1.5em",
                marginRight: "8px",
              }}
            />
            Loading best config and rule weights…
          </div>
        )}

        {!loading && error && (
          <p style={{ margin: "0 0 12px", color: "#c62828" }}>{error}</p>
        )}

        {!loading && data && (
          <>
            <p style={{ margin: "0 0 8px" }}>
              <strong>Config #{data.configId}</strong> ·{" "}
              {formatDate(data.configCreated)}
            </p>
            <p style={{ margin: "0 0 12px", color: "#555" }}>
              TP: {score.truePositives ?? "—"} · FP:{" "}
              {score.falsePositives ?? "—"} · Effective ratio:{" "}
              {score.effectiveRatio != null
                ? Number(score.effectiveRatio).toFixed(2)
                : "—"}
            </p>
            <p
              style={{
                margin: "0 0 12px",
                padding: "8px 10px",
                background: "#fff8e1",
                border: "1px solid #ffe082",
                borderRadius: "4px",
              }}
            >
              ⚠ Any existing manual weights will be cleared. Only the rules
              checked below will be set as new manual weights.
            </p>

            <table
              className="config-table"
              style={{ width: "100%", tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "32px" }} />
                <col style={{ width: "28px" }} />
                <col />
                <col style={{ width: "60px" }} />
                <col style={{ width: "70px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>✓</th>
                  <th>#</th>
                  <th>Rule</th>
                  <th>Disc.</th>
                  <th>Weight</th>
                </tr>
              </thead>
              <tbody>
                {data.ruleWeights.map((rw, idx) => {
                  const isTop = idx < TOP_N;
                  const isChecked = !!selectedRules[rw.weightFieldName];
                  return (
                    <tr
                      key={rw.ruleScoreColumnName}
                      style={isTop ? { fontWeight: "bold" } : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => onToggleRule(rw.weightFieldName)}
                          disabled={applying}
                        />
                      </td>
                      <td>{idx + 1}</td>
                      <td
                        title={rw.ruleScoreColumnName}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {shortRuleName(rw.ruleScoreColumnName)}
                      </td>
                      <td
                        style={{
                          color: discriminationColor(rw.discriminationRatio),
                        }}
                      >
                        {formatNumber(rw.discriminationRatio, 2)}
                      </td>
                      <td>{formatNumber(rw.weight, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        <div className="modal-actions">
          {!loading && data && (
            <span style={{ marginRight: "auto", color: "#555" }}>
              {selectedCount} of {data.ruleWeights.length} selected
            </span>
          )}
          <button onClick={onCancel} disabled={applying}>
            {loading || error || !data ? "Close" : "Cancel"}
          </button>
          {data && !error && (
            <button
              onClick={onConfirm}
              disabled={applying || loading || selectedCount === 0}
            >
              {applying ? "Applying…" : "Apply rules"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Component;
